/**
 * @flow
 */

import React, { Fragment, useEffect } from 'react'
import { Switch, Route, Redirect } from 'react-router-dom'
import Loadable from 'react-loadable'
import { injectGlobal } from 'styled-components'
import { hot } from 'react-hot-loader'
import { ScrollToTop, Loader } from '../common/components'
import { colors } from '../common/theme'
import NexaBold from './assets/nexa-bold.otf'
import NexaLight from './assets/nexa-light.otf'
import Header from '../features/components/Header'
import Sidebar from '../features/components/Sidebar'
import { ClickProvider } from '../features/context'
import ProtectedRoute from '../common/components/ProtectedRoute'
import { TenantProvider } from '../context/tenantConfig'
import { isTokenValid } from './utils/auth'
import {
  GlobalErrorBoundary,
  initializeErrorTracker
} from './utils/ErrorVerify'
import { ToastProvider } from '../context/toastContext'
import CandidateHeader from './pages/candidate/CandidateHeader'

injectGlobal`
  * {
    box-sizing: border-box;
  }

  html, body, #app {
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    font-family: -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    font-size: 0.95em;
    background: ${colors.background};
    color: ${colors.foreground};
  }

  ::selection {
    background: ${colors.secondary};
    color: white;
  }

  ::-moz-selection {
    background: ${colors.secondary};
    color: white;
  }

  .grabbing {
    cursor: move; /* fallback */
    cursor: grabbing;
  }

  @font-face {
    font-family: NexaBold;
    src: url('${NexaBold}') format('opentype');
  }

  @font-face {
    font-family: NexaLight;
    src: url('${NexaLight}') format('opentype');
  }
`

const Dashboard = Loadable({
  loader: () => import('./pages/Dashboard'),
  loading: Loader
})

const Login = Loadable({
  loader: () => import('./pages/Login'),
  loading: Loader
})

const LoadableCandidate = Loadable({
  loader: () => import('./pages/Candidates'),
  loading: Loader
})

const LoadablePreview = Loadable({
  loader: () => import('../features/preview/components'),
  loading: Loader
})

const Pipelines = Loadable({
  loader: () => import('./pages/PipelineList'),
  loading: Loader
})

// Personal "Interviews" management screen — every interview on a pipeline
// assigned to the logged-in user, across all pipelines, regardless of who
// scheduled it. Open to every role; admin-wide visibility is a deferred
// enhancement (see Interviews.js).
const InterviewsLoader = Loadable({
  loader: () => import('./pages/Interviews'),
  loading: Loader
})

const PipelinesDetails = Loadable({
  loader: () => import('./pages/Pipelines'),
  loading: Loader
})

//Added for test pipeline list and details
const TestPipelinesLoader = Loadable({
  loader: () => import('./pages/TestPipelineList'),
  loading: Loader
})
const TestPipelinesDetails = Loadable({
  loader: () => import('./pages/TestPipelines'),
  loading: Loader
})

const UserManagement = Loadable({
  loader: () => import('./pages/UserManagement'),
  loading: Loader
})

const SettingLoader = Loadable({
  loader: () => import('./pages/Setting'),
  loading: Loader
})

const CompanyLoader = Loadable({
  loader: () => import('./pages/Company'),
  loading: Loader
})

const JobProfileLoader = Loadable({
  loader: () => import('./pages/JobProfiles'),
  loading: Loader
})

const AwaitingLoader = Loadable({
  loader: () => import('./pages/PendingApproved'),
  loading: Loader
})

const OperationalChecklistsLoader = Loadable({
  loader: () => import('./pages/OperationalChecklists'),
  loading: Loader
})

const InterviewRemindersLoader = Loadable({
  loader: () => import('./pages/InterviewReminders'),
  loading: Loader
})

const InterviewAcknowledgmentsLoader = Loadable({
  loader: () => import('./pages/InterviewAcknowledgments'),
  loading: Loader
})

const InterviewConfirmLoader = Loadable({
  loader: () => import('./pages/InterviewConfirm'),
  loading: Loader
})

const TemplatesLoader = Loadable({
  loader: () => import('./pages/TemplatesManagement'),
  loading: Loader
})

const CandidateDashboard = Loadable({
  loader: () => import('./pages/candidate/CandidateDashboard'),
  loading: Loader
})
const CandidateProfile = Loadable({
  loader: () => import('./pages/candidate/CandidateProfile'),
  loading: Loader
})
const AppliedJobs = Loadable({
  loader: () => import('./pages/candidate/AppliedJobs'),
  loading: Loader
})

const CandidateLogin = Loadable({
  loader: () => import('./pages/candidate/CandidateLogin'),
  loading: Loader
})

const CandidateFindJobs = Loadable({
  loader: () => import('./pages/candidate/FindJobs'),
  loading: Loader
})

const JobDetails = Loadable({
  loader: () => import('./pages/candidate/JobDetails'),
  loading: Loader
})

const CompanyDetails = Loadable({
  loader: () => import('./pages/candidate/CompanyDetails'),
  loading: Loader
})

const LoadableError404 = Loadable({
  loader: () => import('./pages/Error404'),
  loading: Loader
})

// Create a layout component that includes Header and Sidebar
const MainLayout = ({ children }) => (
  <Fragment>
    <Header />
    <Sidebar />
    {children}
  </Fragment>
)

const CandidateMainLayout = ({ children }) => (
  <Fragment>
    <CandidateHeader />
    {children}
  </Fragment>
)

const ProtectedLayout = ({ children }) => (
  <TenantProvider>
    <MainLayout>{children}</MainLayout>
  </TenantProvider>
)

const CandidateLayout = ({ children }) => (
  <TenantProvider>
    <CandidateMainLayout>{children}</CandidateMainLayout>
  </TenantProvider>
)

function App() {
  const token = localStorage.getItem('token')

  useEffect(() => {
    initializeErrorTracker()
  }, [])

  return (
    <GlobalErrorBoundary>
      <ToastProvider>
        <ClickProvider>
          <ScrollToTop>
            <Switch>
              <ProtectedRoute
                allowedRoles={['recruiter', 'manager', 'admin', 'sr recruiter']}
                exact
                path="/dashboard"
                render={() => (
                  <ProtectedLayout>
                    <Dashboard />
                  </ProtectedLayout>
                )}
              />

              <ProtectedRoute
                allowedRoles={['recruiter', 'manager', 'admin', 'sr recruiter']}
                exact
                path="/candidates"
                render={() => (
                  <ProtectedLayout>
                    <LoadableCandidate />
                  </ProtectedLayout>
                )}
              />

              <ProtectedRoute
                allowedRoles={['recruiter', 'manager', 'admin', 'sr recruiter']}
                path="/candidate/resume"
                render={() => (
                  <ProtectedLayout>
                    <LoadablePreview />
                  </ProtectedLayout>
                )}
              />

              <ProtectedRoute
                allowedRoles={['recruiter', 'manager', 'admin', 'sr recruiter']}
                exact
                path="/pipelines"
                render={() => (
                  <ProtectedLayout>
                    <Pipelines />
                  </ProtectedLayout>
                )}
              />

              <ProtectedRoute
                allowedRoles={['recruiter', 'manager', 'admin', 'sr recruiter']}
                exact
                path="/interviews"
                render={() => (
                  <ProtectedLayout>
                    <InterviewsLoader />
                  </ProtectedLayout>
                )}
              />

              <ProtectedRoute
                allowedRoles={['recruiter', 'manager', 'admin', 'sr recruiter']}
                path="/pipeline/:id"
                render={() => (
                  <ProtectedLayout>
                    <PipelinesDetails />
                  </ProtectedLayout>
                )}
              />

              {/* Added for test pipeline list and details */}
              <ProtectedRoute
                allowedRoles={['recruiter', 'manager', 'admin', 'sr recruiter']}
                exact
                path="/test-pipelines"
                render={() => (
                  <ProtectedLayout>
                    <TestPipelinesLoader />
                  </ProtectedLayout>
                )}
              />

              <ProtectedRoute
                allowedRoles={['recruiter', 'manager', 'admin', 'sr recruiter']}
                path="/test-pipeline/:id"
                render={() => (
                  <ProtectedLayout>
                    <TestPipelinesDetails />
                  </ProtectedLayout>
                )}
              />

              <ProtectedRoute
                allowedRoles={['recruiter', 'manager', 'admin', 'sr recruiter']}
                path="/user-management"
                render={() => (
                  <ProtectedLayout>
                    <UserManagement />
                  </ProtectedLayout>
                )}
              />
              <ProtectedRoute
                allowedRoles={['recruiter', 'manager', 'admin', 'sr recruiter']}
                path="/templates"
                render={() => (
                  <ProtectedLayout>
                    <TemplatesLoader />
                  </ProtectedLayout>
                )}
              />
              <ProtectedRoute
                allowedRoles={['manager', 'admin']}
                path="/operational-checklists"
                render={() => (
                  <ProtectedLayout>
                    <OperationalChecklistsLoader />
                  </ProtectedLayout>
                )}
              />
              <ProtectedRoute
                allowedRoles={['admin']}
                path="/interview-reminders"
                render={() => (
                  <ProtectedLayout>
                    <InterviewRemindersLoader />
                  </ProtectedLayout>
                )}
              />
              <ProtectedRoute
                allowedRoles={['admin']}
                path="/interview-acknowledgments"
                render={() => (
                  <ProtectedLayout>
                    <InterviewAcknowledgmentsLoader />
                  </ProtectedLayout>
                )}
              />
              <ProtectedRoute
                allowedRoles={['recruiter', 'manager', 'admin', 'sr recruiter']}
                path="/setting"
                render={() => (
                  <ProtectedLayout>
                    <SettingLoader />
                  </ProtectedLayout>
                )}
              />
              <ProtectedRoute
                allowedRoles={['recruiter', 'manager', 'admin', 'sr recruiter']}
                path="/companies"
                render={() => (
                  <ProtectedLayout>
                    <CompanyLoader />
                  </ProtectedLayout>
                )}
              />
              <ProtectedRoute
                allowedRoles={['recruiter', 'manager', 'admin', 'sr recruiter']}
                path="/job-profiles"
                render={() => (
                  <ProtectedLayout>
                    <JobProfileLoader />
                  </ProtectedLayout>
                )}
              />
              <ProtectedRoute
                allowedRoles={['recruiter', 'manager', 'admin', 'sr recruiter']}
                path="/pending-for-approval"
                render={() => (
                  <ProtectedLayout>
                    <AwaitingLoader />
                  </ProtectedLayout>
                )}
              />

              <ProtectedRoute
                allowedRoles={['candidate']}
                path="/c/dashboard"
                render={() => (
                  <CandidateLayout>
                    <CandidateDashboard />
                  </CandidateLayout>
                )}
              />
              <ProtectedRoute
                allowedRoles={['candidate']}
                path="/c/profile"
                render={() => (
                  <CandidateLayout>
                    <CandidateProfile />
                  </CandidateLayout>
                )}
              />
              <ProtectedRoute
                allowedRoles={['candidate']}
                path="/c/applied-jobs"
                render={() => (
                  <CandidateLayout>
                    <AppliedJobs />
                  </CandidateLayout>
                )}
              />
              <ProtectedRoute
                allowedRoles={['candidate']}
                path="/c/jobs"
                render={() => (
                  <CandidateLayout>
                    <CandidateFindJobs />
                  </CandidateLayout>
                )}
              />
              <ProtectedRoute
                allowedRoles={['candidate']}
                path="/c/job/:id-:title"
                render={() => (
                  <CandidateLayout>
                    <JobDetails />
                  </CandidateLayout>
                )}
              />
              <ProtectedRoute
                allowedRoles={['candidate']}
                path="/c/company/sigma-consultant"
                render={() => (
                  <CandidateLayout>
                    <CompanyDetails />
                  </CandidateLayout>
                )}
              />

              <ProtectedRoute
                path="/c/login"
                render={() => <CandidateLogin />}
              />

              <Route
                exact
                path="/"
                component={() => <Redirect to="/dashboard" />}
              />
              <Route
                exact
                path="/login"
                component={
                  isTokenValid(token)
                    ? () => <Redirect to="/dashboard" />
                    : Login
                }
              />

              {/* PUBLIC — candidate interview attendance confirmation (no auth) */}
              <Route
                exact
                path="/interview/confirm"
                component={InterviewConfirmLoader}
              />

              <ProtectedRoute path="*" render={() => <LoadableError404 />} />

              <ProtectedRoute
                path="/unauthorized"
                render={() => <LoadableError404 />}
              />
            </Switch>
          </ScrollToTop>
        </ClickProvider>
      </ToastProvider>
    </GlobalErrorBoundary>
  )
}

export default hot(module)(App)
